import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { signIn, confirmSignIn, fetchUserAttributes, fetchAuthSession, resendSignUpCode } from '@aws-amplify/auth';
import { useAuth } from './AuthContext.js';
import { EMAIL_LINK_OPTIONS } from './emailLinkOptions';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const isMobileApp = process.env.REACT_APP_PLATFORM === 'ios' || process.env.REACT_APP_PLATFORM === 'android';

function SignIn() {
  const location = useLocation();
  // Coming from /verify: pre-fill the email so they only need their password
  const verifiedEmail = location.state?.verified ? location.state.email : '';
  const [email, setEmail] = useState(verifiedEmail || '');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState(verifiedEmail ? 'Email verified! Sign in to start studying.' : '');
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [needsNewPassword, setNeedsNewPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const navigate = useNavigate();
  const { setIsAuthenticated, setUserEmail, setIsSubscribed, setTotalQuizzesTaken, setLastQuizScore } = useAuth();

  const handleSignIn = async (e) => {
    e.preventDefault();
    setMessage('');
    setIsLoading(true);

    try {
      const { nextStep } = await signIn({ username: email, password });

      if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setNeedsNewPassword(true);
        setMessage('Please set a new password to continue.');
        setIsError(false);
        setIsLoading(false);
        return;
      }

      if (nextStep.signInStep === 'CONFIRM_SIGN_UP') {
        // Correct password but email never verified - send a fresh code and finish verification
        await sendToVerify();
        return;
      }

      const attributes = await fetchUserAttributes();
      const userEmail = attributes.email;

      setIsAuthenticated(true);
      setUserEmail(userEmail);
      setMessage('Signed in successfully!');
      setIsError(false);

      const { idToken } = (await fetchAuthSession()).tokens ?? {};
      if (!idToken) {
        throw new Error('No ID token available');
      }

      try {
        const { data } = await axios.get(`${API_BASE_URL}/api/auth/subscription-status`, {
          headers: {
            Authorization: `Bearer ${idToken}`,
            'Content-Type': 'application/json'
          },
          timeout: 10000
        });
        setIsSubscribed(data.isSubscribed);
        setTotalQuizzesTaken(data.totalQuizzesTaken || 0);
        setLastQuizScore(data.lastQuizScore ?? null);

        if (data.isSubscribed || isMobileApp) {
          setTimeout(() => navigate('/', { state: { userId: userEmail } }), 1000);
        } else {
          const { from, plan } = location.state || {};
          if (from === '/pricing' && plan) {
            try {
              const res = await axios.post(`${API_BASE_URL}/api/auth/create-checkout-session`, {
                planId: plan,
                origin: window.location.origin
              }, {
                headers: {
                  Authorization: `Bearer ${idToken}`,
                  'Content-Type': 'application/json'
                },
                timeout: 10000
              });
              window.location.href = res.data.url;
            } catch (err) {
              console.error('SignIn: Stripe checkout error:', err);
              setMessage('Failed to load payment—redirecting to pricing.');
              setIsError(true);
              setTimeout(() => navigate('/pricing'), 2000);
            }
          } else {
            setTimeout(() => navigate('/', { state: { userId: userEmail } }), 1000);
          }
        }
      } catch (subscriptionErr) {
        setIsSubscribed(false);

        const { from, plan } = location.state || {};
        if (from === '/pricing' && plan) {
          setTimeout(() => navigate('/pricing'), 1000);
        } else {
          setTimeout(() => navigate('/', { state: { userId: userEmail } }), 1000);
        }
      }
    } catch (err) {
      console.error('SignIn: Sign-in error:', err);
      if (err.name === 'UserNotConfirmedException') {
        await sendToVerify();
        return;
      }
      setMessage(err.message || 'Sign-in failed');
      setIsError(true);
      setIsLoading(false);
    }
  };

  const sendToVerify = async () => {
    try {
      await resendSignUpCode({ username: email, options: EMAIL_LINK_OPTIONS });
      setMessage('Your email isn\'t verified yet. We sent you a new code.');
      setIsError(false);
    } catch (err) {
      // Still send them to /verify - they may already have a valid code
      console.error('SignIn: Resend code error:', err);
    }
    setTimeout(() => navigate('/verify', { state: { email } }), 1500);
  };

  const handleNewPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmNewPassword) {
      setMessage('Passwords do not match.');
      setIsError(true);
      return;
    }
    setIsLoading(true);
    setMessage('');
    try {
      await confirmSignIn({ challengeResponse: newPassword });
      const attributes = await fetchUserAttributes();
      const userEmail = attributes.email;

      setIsAuthenticated(true);
      setUserEmail(userEmail);
      setIsError(false);

      const { idToken } = (await fetchAuthSession()).tokens ?? {};
      const { data } = await axios.get(`${API_BASE_URL}/api/auth/subscription-status`, {
        headers: { Authorization: `Bearer ${idToken}`, 'Content-Type': 'application/json' },
        timeout: 10000
      });
      setIsSubscribed(data.isSubscribed);
      setTotalQuizzesTaken(data.totalQuizzesTaken || 0);
      setLastQuizScore(data.lastQuizScore ?? null);
      navigate('/', { state: { userId: userEmail } });
    } catch (err) {
      console.error('SignIn: New password error:', err);
      setMessage(err.message || 'Failed to set new password.');
      setIsError(true);
      setIsLoading(false);
    }
  };

  if (needsNewPassword) {
    return (
      <div className="min-h-screen bg-white font-quicksand flex flex-col md:flex-row">
        <div className="hidden md:flex md:w-1/2 bg-gradient-to-br from-study-pink via-study-teal to-study-yellow p-12 flex-col justify-center items-center text-center">
          <img src="/studyPassLogo2.png" alt="StudyPass Logo" className="w-32 h-32 mb-8 rounded-full border-4 border-white shadow-lg" />
          <h1 className="text-4xl font-bold text-study-black mb-4">Almost There!</h1>
          <p className="text-xl text-study-black max-w-md">Set a new password to access your account.</p>
        </div>
        <div className="flex-1 flex flex-col justify-center items-center p-8 md:p-12">
          <div className="w-full max-w-md">
            <div className="md:hidden text-center mb-8">
              <img src="/studyPassLogo2.png" alt="StudyPass Logo" className="w-20 h-20 mx-auto mb-4 rounded-full border-4 border-study-teal shadow-lg" />
            </div>
            <h2 className="text-3xl font-bold text-study-black mb-2 text-center md:text-left">Set New Password</h2>
            <p className="text-gray-600 mb-8 text-center md:text-left">Choose a permanent password for your account.</p>
            <form onSubmit={handleNewPassword} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-study-black mb-2">New Password</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">🔒</span>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    className="w-full pl-12 pr-12 py-3 border-2 border-gray-200 rounded-xl text-study-black focus:outline-none focus:border-study-teal transition-all"
                    required
                    disabled={isLoading}
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-study-black mb-2">Confirm New Password</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">🔒</span>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    placeholder="Confirm new password"
                    className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl text-study-black focus:outline-none focus:border-study-teal transition-all"
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>
              {message && (
                <div className={`p-4 rounded-xl text-center ${isError ? 'bg-red-100 border border-red-300 text-red-700' : 'bg-green-100 border border-green-300 text-green-700'}`}>
                  {message}
                </div>
              )}
              <button
                type="submit"
                className="w-full bg-gradient-to-r from-study-teal to-study-pink text-study-black font-bold text-lg py-4 rounded-xl hover:shadow-lg transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                disabled={isLoading}
              >
                {isLoading ? <span className="flex items-center justify-center gap-2"><span className="animate-spin">⏳</span> Setting Password...</span> : 'Set New Password'}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white font-quicksand flex flex-col md:flex-row">
      {/* Left Side - Branding */}
      <div className="hidden md:flex md:w-1/2 bg-gradient-to-br from-study-pink via-study-teal to-study-yellow p-12 flex-col justify-center items-center text-center">
        <img
          src="/studyPassLogo2.png"
          alt="StudyPass Logo"
          className="w-32 h-32 mb-8 rounded-full border-4 border-white shadow-lg"
        />
        <h1 className="text-4xl font-bold text-study-black mb-4">
          Welcome Back!
        </h1>
        <p className="text-xl text-study-black mb-8 max-w-md">
          Continue your journey to acing the dental hygiene boards.
        </p>
        <div className="flex gap-6 text-study-black">
          <div className="text-center">
            <div className="text-3xl mb-1">📝</div>
            <div className="text-sm font-semibold">Practice Quizzes</div>
          </div>
          <div className="text-center">
            <div className="text-3xl mb-1">📚</div>
            <div className="text-sm font-semibold">Study Guides</div>
          </div>
          <div className="text-center">
            <div className="text-3xl mb-1">🗂️</div>
            <div className="text-sm font-semibold">Flashcards</div>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex flex-col justify-center items-center p-8 md:p-12">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="md:hidden text-center mb-8">
            <img
              src="/studyPassLogo2.png"
              alt="StudyPass Logo"
              className="w-20 h-20 mx-auto mb-4 rounded-full border-4 border-study-teal shadow-lg"
            />
          </div>

          <h2 className="text-3xl font-bold text-study-black mb-2 text-center md:text-left">
            Sign In
          </h2>
          <p className="text-gray-600 mb-8 text-center md:text-left">
            Enter your credentials to access your account
          </p>

          <form onSubmit={handleSignIn} className="space-y-5">
            {/* Email Field */}
            <div>
              <label className="block text-sm font-semibold text-study-black mb-2">
                Email Address
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                  ✉️
                </span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl text-study-black focus:outline-none focus:border-study-teal transition-all"
                  required
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label className="block text-sm font-semibold text-study-black mb-2">
                Password
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                  🔒
                </span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full pl-12 pr-12 py-3 border-2 border-gray-200 rounded-xl text-study-black focus:outline-none focus:border-study-teal transition-all"
                  required
                  disabled={isLoading}
                  autoFocus={!!verifiedEmail}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
            </div>

            {/* Message */}
            {message && (
              <div className={`p-4 rounded-xl text-center ${
                isError
                  ? 'bg-red-100 border border-red-300 text-red-700'
                  : 'bg-green-100 border border-green-300 text-green-700'
              }`}>
                {message}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              className="w-full bg-gradient-to-r from-study-teal to-study-pink text-study-black font-bold text-lg py-4 rounded-xl hover:shadow-lg transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="animate-spin">⏳</span> Signing In...
                </span>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Forgot Password */}
          <div className="text-right mt-2">
            <Link
              to="/forgot-password"
              className="text-sm text-study-teal font-semibold hover:text-study-pink transition-all"
            >
              Forgot Password?
            </Link>
          </div>

          {/* Divider */}
          <div className="flex items-center my-8">
            <div className="flex-1 border-t border-gray-200"></div>
            <span className="px-4 text-gray-500 text-sm">or</span>
            <div className="flex-1 border-t border-gray-200"></div>
          </div>

          {/* Sign Up Link */}
          {!isMobileApp && (
            <div className="text-center">
              <p className="text-gray-600">
                Don't have an account?{' '}
                <Link
                  to="/SignUp"
                  className="text-study-teal font-bold hover:text-study-pink transition-all"
                >
                  Sign Up Free
                </Link>
              </p>
            </div>
          )}
          {isMobileApp && (
            <div className="text-center">
              <p className="text-gray-600 text-sm">
                Don't have an account? Visit{' '}
                <span className="text-study-teal font-bold">studypassrdh.com</span>{' '}
                to sign up.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SignIn;
